import { Component } from './live-codec.js';
import { parseResource, resourceValue, resourceProperties } from './vgui-res.js';
import { parseVtf } from './vtf.js';
const clean = value => String(value ?? '').replace(/\x07[\da-f]{6}|\x08[\da-f]{8}/gi, '').replace(/[\x00-\x1f\x7f-\x9f]/g, '');
const colors = ['#eee', '#ccc', '#ff8075', '#99ccff'];
export function noticePresentation(notice) {
    const [killer, victim, assister] = notice.actors;
    const suicide = !killer.userid || killer.userid === victim.userid;
    let weapon = clean(notice.weapon).replace(/^tf_weapon_/, '');
    if (notice.custom === 1) weapon = weapon === 'huntsman' ? 'headshot_bow' : 'headshot';
    if (notice.custom === 2) weapon = 'backstab';
    if (suicide && !weapon) weapon = 'skull';
    const humiliation = notice.kind >= 2;
    return { killer: suicide ? '' : clean(killer.name), victim: clean(victim.name), assister: suicide ? '' : clean(assister.name),
        icon: `d_${weapon || 'skull'}`, weapon: weapon.replaceAll('_', ' '), critical: !!(notice.damage & (1 << 20)),
        info: humiliation ? (notice.custom === 39 || notice.custom === 80 || notice.flags & 32 ? 'HUMILIATION!' : `HIT ×${notice.count ?? 1}`)
            : notice.kind === 1 ? ['Dispenser', 'Teleporter', 'Sentry'][notice.object] ?? 'Building' : '',
        rivalry: notice.flags & 5 ? notice.flags & 4 ? 'Revenge' : 'Domination' : '' };
}
export function deathNoticeIcons(bytes) {
    const entries = parseResource(new TextDecoder().decode(bytes));
    const data = entries.find(entry => Array.isArray(entry.value))?.value ?? [];
    const refs = resourceValue(data, 'texturefilerefs') ?? [];
    const icons = new Map();
    for (const entry of resourceValue(data, 'texturedata') ?? []) {
        const icon = resourceProperties(entry.value);
        if (icon.file) icons.set(entry.key, icon);
        for (const ref of refs) if (icon[ref.key]) {
            const prefix = resourceProperties(ref.value).prefix ?? '';
            icons.set(prefix + entry.key, { ...icon, file: icon[ref.key] });
        }
    }
    return icons;
}
export class DeathNoticeState {
    constructor() { this.reset(); }
    reset() { this.rows = []; this.seen = new Set(); this.epoch = null; }
    apply(message) {
        if (message.epoch !== this.epoch) { this.reset(); this.epoch = message.epoch; }
        for (const change of message.changes ?? []) {
            const notice = change.components[Component.DeathNotice];
            if (!notice || this.seen.has(change.key)) continue;
            this.seen.add(change.key);
            if (this.seen.size > 4096) this.seen.delete(this.seen.values().next().value);
            if (notice.startTime + 6000 <= message.sampleTime) continue;
            const previous = notice.kind >= 2 && this.rows.find(row => row.kind === notice.kind && row.actors[0].userid === notice.actors[0].userid && row.actors[1].userid === notice.actors[1].userid && row.startTime + 6000 > notice.startTime);
            if (previous) { Object.assign(previous, notice, { count: previous.count + 1 }); }
            else this.rows.push({ ...notice, count: 1 });
        }
        this.frame(message.sampleTime);
    }
    frame(time) { this.rows = this.rows.filter(row => row.startTime + 6000 > time).slice(-4); }
}
export class LiveDeathNotices {
    constructor({ root, cache, diagnostic }) { Object.assign(this, { root, cache, diagnostic }); this.state = new DeathNoticeState(); this.icons = new Map(); this.textures = new Map(); }
    apply(message) { this.state.apply(message); this.render(); }
    frame(time) { const count = this.state.rows.length; this.state.frame(time); if (count !== this.state.rows.length) this.render(); }
    reset() { this.state.reset(); this.render(); }
    async load(assetSet) {
        if (!assetSet || this.assetSet === assetSet) return;
        this.assetSet = assetSet; this.abort?.abort(); const abort = this.abort = new AbortController(); this.icons.clear(); this.textures.clear();
        try {
            const bytes = await this.cache.fetch(`/assets/scripts/mod_textures.txt?assetSet=${encodeURIComponent(assetSet)}`, abort.signal);
            if (abort.signal.aborted) return;
            this.icons = deathNoticeIcons(bytes); this.renderKey = null;
            this.render();
        } catch (error) { if (!abort.signal.aborted) this.diagnostic?.(`Kill feed icons: ${error.message}`); }
    }
    async icon(canvas, name) {
        const icon = this.icons.get(name) ?? this.icons.get('d_skull'); if (!icon?.file) return;
        const path = icon.file.toLowerCase().replaceAll('\\', '/');
        if (!/^[a-z0-9_/-]+$/.test(path) || path.split('/').some(p => !p || p === '..')) return;
        const abort = this.abort;
        try {
            if (!this.textures.has(path)) this.textures.set(path, this.cache.fetch(`/assets/materials/${path}.vtf?assetSet=${encodeURIComponent(this.assetSet)}`, abort.signal).then(bytes => {
                const mip = parseVtf(bytes, false).faces[0][0]; const atlas = document.createElement('canvas'); atlas.width = mip.width; atlas.height = mip.height;
                atlas.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(mip.data), mip.width, mip.height), 0, 0); return atlas;
            }));
            const atlas = await this.textures.get(path); if (abort.signal.aborted) return;
            const [x, y, width, height] = ['x', 'y', 'width', 'height'].map(key => Number(icon[key]));
            if (![x,y,width,height].every(Number.isFinite) || x < 0 || y < 0 || width <= 0 || height <= 0 || x+width > atlas.width || y+height > atlas.height) return;
            canvas.width = width; canvas.height = height; canvas.style.width = `${width / height * 22}px`;
            canvas.getContext('2d').drawImage(atlas, x, y, width, height, 0, 0, width, height); canvas.hidden = false;
            if (canvas.nextSibling) canvas.nextSibling.hidden = true;
        } catch { /* A readable weapon label remains when an icon is unavailable. */ }
    }
    render() {
        const key = JSON.stringify(this.state.rows);
        if (key === this.renderKey) return;
        this.renderKey = key; this.root.replaceChildren();
        for (const notice of this.state.rows) {
            const view = noticePresentation(notice), row = document.createElement('div'); row.className = 'live-death-notice';
            const text = (value, team) => { const span = document.createElement('span'); span.textContent = value; if (team != null) span.style.color = colors[team]; row.append(span); };
            if (view.rivalry) text(view.rivalry);
            if (view.killer) text(view.killer, notice.actors[0].team);
            if (view.assister) { text('+'); text(view.assister, notice.actors[2].team); }
            const canvas = document.createElement('canvas'); canvas.hidden = true; canvas.setAttribute('aria-hidden','true'); row.append(canvas);
            text(view.weapon); void this.icon(canvas, view.icon);
            if (view.critical) text('CRIT');
            text(view.victim, notice.actors[1].team); if (view.info) text(view.info);
            this.root.append(row);
        }
    }
    dispose() { this.abort?.abort(); this.reset(); }
}
